import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Download, FileUp, FolderOpen, CheckSquare, AlertCircle, CheckCircle2 } from 'lucide-react';
import PageHeader from '../components/layout/PageHeader';
import Button from '../components/ui/Button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { ImportResult } from '../types/api';
import {
  useImportProjects,
  useImportTasks,
  downloadProjectsTemplate,
  downloadTasksTemplate,
} from '../api/import';

interface ImportSectionProps {
  title: string;
  description: string;
  icon: React.ElementType;
  iconBg: string;
  iconCls: string;
  columns: string[];
  isPending: boolean;
  result: ImportResult | undefined;
  onDownloadTemplate: () => void;
  onUpload: (file: File) => void;
}

function ImportSection({
  title,
  description,
  icon: Icon,
  iconBg,
  iconCls,
  columns,
  isPending,
  result,
  onDownloadTemplate,
  onUpload,
}: ImportSectionProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);

  function handleFile(file: File) {
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a .csv file.');
      return;
    }
    setFileName(file.name);
    setPendingFile(file);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  function handleImport() {
    if (!pendingFile) return;
    onUpload(pendingFile);
  }

  const hasFailures = result && result.failures.length > 0;

  return (
    <Card className="overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
        <div className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-md', iconBg)}>
          <Icon className={cn('h-4 w-4', iconCls)} />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        <button
          type="button"
          onClick={onDownloadTemplate}
          className="ml-auto flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <Download className="h-3.5 w-3.5" />
          Template
        </button>
      </div>

      {/* Columns reference */}
      <div className="border-b border-border bg-muted/30 px-5 py-3">
        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Expected columns</p>
        <div className="flex flex-wrap gap-1.5">
          {columns.map((col) => (
            <span key={col} className="rounded bg-muted px-2 py-0.5 font-mono text-[11px] text-foreground">
              {col}
            </span>
          ))}
        </div>
      </div>

      {/* Drop zone */}
      <CardContent className="p-5">
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed py-8 text-center transition-colors',
            dragging
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/50 hover:bg-muted/30',
          )}
        >
          <FileUp className={cn('h-8 w-8', fileName ? 'text-primary' : 'text-muted-foreground/50')} />
          {fileName ? (
            <p className="text-sm font-medium text-foreground">{fileName}</p>
          ) : (
            <>
              <p className="text-sm font-medium text-foreground">Drop your CSV here</p>
              <p className="text-xs text-muted-foreground">or click to browse</p>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
          />
        </div>
      </CardContent>

      {/* Result */}
      {result && (
        <div className={cn('border-t px-5 py-4', hasFailures ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30' : 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30')}>
          <div className="flex items-center gap-2 mb-2">
            {hasFailures
              ? <AlertCircle className="h-4 w-4 text-amber-500 shrink-0" />
              : <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />}
            <p className={cn('text-sm font-medium', hasFailures ? 'text-amber-700 dark:text-amber-400' : 'text-emerald-700 dark:text-emerald-400')}>
              {result.created} row{result.created !== 1 ? 's' : ''} imported successfully
              {hasFailures && ` · ${result.failures.length} skipped`}
            </p>
          </div>
          {hasFailures && (
            <div className="space-y-1">
              {result.failures.map((f) => (
                <p key={f.row} className="text-xs text-amber-700 dark:text-amber-400">
                  <span className="font-medium">Row {f.row}:</span> {f.error}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-border bg-muted/30 px-5 py-4">
        <Button
          onClick={handleImport}
          disabled={!pendingFile}
          loading={isPending}
          size="sm"
        >
          Import {title.toLowerCase()}
        </Button>
      </div>
    </Card>
  );
}

export default function ImportPage() {
  const importProjects = useImportProjects();
  const importTasks = useImportTasks();

  const [projectsResult, setProjectsResult] = useState<ImportResult | undefined>();
  const [tasksResult, setTasksResult] = useState<ImportResult | undefined>();

  function handleImportProjects(file: File) {
    importProjects.mutate(file, {
      onSuccess: (result) => {
        setProjectsResult(result);
        if (result.created > 0) toast.success(`Imported ${result.created} project(s).`);
        else toast.warning('No projects were imported.');
      },
      onError: () => toast.error('Failed to import projects. Check the file format.'),
    });
  }

  function handleImportTasks(file: File) {
    importTasks.mutate(file, {
      onSuccess: (result) => {
        setTasksResult(result);
        if (result.created > 0) toast.success(`Imported ${result.created} task(s).`);
        else toast.warning('No tasks were imported.');
      },
      onError: () => toast.error('Failed to import tasks. Check the file format.'),
    });
  }

  return (
    <div className="max-w-2xl space-y-6">
      <PageHeader
        title="Import"
        description="Bulk-import existing projects and tasks from CSV."
      />

      <p className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-300">
        Download a template, fill it in, and upload. Projects are matched by name — import projects first, then tasks.
        Tasks are linked to projects via <span className="font-mono text-xs">project_name</span> and engineers via <span className="font-mono text-xs">assignee_email</span>.
      </p>

      <ImportSection
        title="Projects"
        description="Create multiple projects in one go."
        icon={FolderOpen}
        iconBg="bg-primary/10"
        iconCls="text-primary"
        columns={['name *', 'description']}
        isPending={importProjects.isPending}
        result={projectsResult}
        onDownloadTemplate={downloadProjectsTemplate}
        onUpload={handleImportProjects}
      />

      <ImportSection
        title="Tasks"
        description="Import tasks and link them to existing projects."
        icon={CheckSquare}
        iconBg="bg-violet-50 dark:bg-violet-950/40"
        iconCls="text-violet-600 dark:text-violet-400"
        columns={['project_name *', 'title *', 'description', 'points *', 'due_date *', 'type', 'assignee_email']}
        isPending={importTasks.isPending}
        result={tasksResult}
        onDownloadTemplate={downloadTasksTemplate}
        onUpload={handleImportTasks}
      />
    </div>
  );
}
